import React from 'react';
import { formatPercentage, formatNumber } from '../lib/formatters';

// Color scheme for ownership breakdown
const COLORS = {
  owned: '#22C55E',      // Green - Your shares
  onSale: '#F59E0B',     // Amber - On sale in marketplace
  available: '#6B7280',  // Gray - Available to buy
  line: '#1F2937',       // Dark gray for lines
};

export const VisualTruckOwnership = ({ 
  totalShares = 100,
  yourShares = 0,
  onSaleShares = 0,
  soldShares = 0,
  className = '' 
}) => {
  const availableShares = totalShares - soldShares;
  const otherOwnedShares = soldShares - yourShares - onSaleShares;
  
  // Calculate percentages
  const yourPercent = (yourShares / totalShares) * 100;
  const onSalePercent = (onSaleShares / totalShares) * 100;
  const availablePercent = (availableShares / totalShares) * 100;
  const otherOwnedPercent = (otherOwnedShares / totalShares) * 100;

  return (
    <div className={`relative ${className}`} data-testid="visual-truck-ownership">
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl p-6 md:p-8">
        {/* Detailed Truck + Trailer SVG - Clean line art style */}
        <svg viewBox="0 0 1000 280" className="w-full h-auto mb-4">
          <defs>
            {/* Gradient showing ownership breakdown */}
            <linearGradient id="truckOwnershipGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor={COLORS.owned} />
              <stop offset={`${yourPercent}%`} stopColor={COLORS.owned} />
              <stop offset={`${yourPercent}%`} stopColor={COLORS.onSale} />
              <stop offset={`${yourPercent + onSalePercent}%`} stopColor={COLORS.onSale} />
              <stop offset={`${yourPercent + onSalePercent}%`} stopColor="#3B82F6" />
              <stop offset={`${yourPercent + onSalePercent + otherOwnedPercent}%`} stopColor="#3B82F6" />
              <stop offset={`${yourPercent + onSalePercent + otherOwnedPercent}%`} stopColor={COLORS.available} />
              <stop offset="100%" stopColor={COLORS.available} />
            </linearGradient>
          </defs>

          {/* TRUCK CAB - Clean line art */}
          <g stroke={COLORS.line} strokeWidth="2" fill="none">
            {/* Main cab outline */}
            <path d="M 60 160 L 60 120 L 80 120 L 80 100 L 140 100 L 150 85 L 170 85 L 180 100 L 180 160" />
            
            {/* Cab body fill */}
            <path 
              d="M 60 160 L 60 120 L 80 120 L 80 100 L 140 100 L 150 85 L 170 85 L 180 100 L 180 160 Z"
              fill="url(#truckOwnershipGradient)"
              strokeWidth="2.5"
            />
            
            {/* Windshield */}
            <path d="M 140 100 L 150 88 L 168 88 L 175 100 L 140 100" fill="#87CEEB" opacity="0.6" />
            
            {/* Side window */}
            <rect x="95" y="105" width="40" height="25" fill="#87CEEB" opacity="0.6" rx="2" />
            
            {/* Door line */}
            <line x1="90" y1="135" x2="90" y2="160" />
            
            {/* Door handle */}
            <circle cx="115" cy="145" r="2" fill={COLORS.line} />
            
            {/* Front grille */}
            <rect x="175" y="115" width="8" height="45" />
            
            {/* Exhaust stack */}
            <rect x="148" y="70" width="5" height="15" />
            
            {/* Front wheel (steer) */}
            <circle cx="100" cy="175" r="18" fill="#1F2937" strokeWidth="2.5" />
            <circle cx="100" cy="175" r="10" fill="#4B5563" />
            <circle cx="100" cy="175" r="4" fill="#9CA3AF" />
            
            {/* Rear wheels (drive - dual) */}
            <circle cx="155" cy="175" r="18" fill="#1F2937" strokeWidth="2.5" />
            <circle cx="155" cy="175" r="10" fill="#4B5563" />
            <circle cx="170" cy="175" r="18" fill="#1F2937" strokeWidth="2.5" />
            <circle cx="170" cy="175" r="10" fill="#4B5563" />
          </g>

          {/* TRAILER - Clean box trailer style */}
          <g stroke={COLORS.line} strokeWidth="2" fill="none">
            {/* Main trailer body */}
            <rect
              x="195"
              y="80"
              width="720"
              height="95"
              fill="url(#truckOwnershipGradient)"
              strokeWidth="2.5"
              rx="2"
            />
            
            {/* Top edge line */}
            <line x1="200" y1="85" x2="910" y2="85" strokeWidth="1" opacity="0.3" stroke="#fff" />
            
            {/* Panel lines */}
            <line x1="380" y1="82" x2="380" y2="173" strokeWidth="1.5" opacity="0.3" />
            <line x1="565" y1="82" x2="565" y2="173" strokeWidth="1.5" opacity="0.3" />
            <line x1="750" y1="82" x2="750" y2="173" strokeWidth="1.5" opacity="0.3" />
            
            {/* Rear door */}
            <rect x="900" y="90" width="10" height="80" fill="#374151" rx="1" />
            <rect x="903" y="120" width="4" height="20" fill="#6B7280" rx="1" />
            
            {/* Undercarriage line */}
            <line x1="200" y1="177" x2="900" y2="177" strokeWidth="1" opacity="0.5" />
            
            {/* LANDING GEAR - Front of trailer */}
            <rect x="240" y="175" width="6" height="45" fill="#4B5563" strokeWidth="2" />
            <rect x="235" y="218" width="16" height="5" fill="#6B7280" strokeWidth="1.5" rx="1" />
            
            <rect x="275" y="175" width="6" height="45" fill="#4B5563" strokeWidth="2" />
            <rect x="270" y="218" width="16" height="5" fill="#6B7280" strokeWidth="1.5" rx="1" />
            
            {/* Crank handle */}
            <circle cx="258" cy="185" r="4" fill="#9CA3AF" />
            
            {/* REAR WHEELS - 2 tandem wheels side by side */}
            <circle cx="820" cy="180" r="20" fill="#1F2937" strokeWidth="2.5" />
            <circle cx="820" cy="180" r="12" fill="#4B5563" />
            <circle cx="820" cy="180" r="5" fill="#9CA3AF" />
            
            <circle cx="860" cy="180" r="20" fill="#1F2937" strokeWidth="2.5" />
            <circle cx="860" cy="180" r="12" fill="#4B5563" />
            <circle cx="860" cy="180" r="5" fill="#9CA3AF" />
          </g>
          
          {/* Ground line */}
          <line x1="40" y1="223" x2="960" y2="223" stroke="#374151" strokeWidth="1.5" opacity="0.3" />
        </svg>

        {/* Ownership Breakdown Legend */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="bg-gray-800/50 rounded-lg p-3 text-center border border-gray-700">
            <div className="flex items-center justify-center gap-2 mb-1">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.owned }} />
              <span className="text-gray-400 text-xs uppercase tracking-wide">You Own</span>
            </div>
            <div className="text-xl font-bold text-green-400 font-mono">{formatNumber(yourShares)}</div>
            <div className="text-xs text-gray-500">{formatPercentage(yourPercent)}</div>
          </div>
          
          <div className="bg-gray-800/50 rounded-lg p-3 text-center border border-gray-700">
            <div className="flex items-center justify-center gap-2 mb-1">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.onSale }} />
              <span className="text-gray-400 text-xs uppercase tracking-wide">On Sale</span>
            </div>
            <div className="text-xl font-bold text-amber-400 font-mono">{formatNumber(onSaleShares)}</div>
            <div className="text-xs text-gray-500">{formatPercentage(onSalePercent)}</div>
          </div>
          
          <div className="bg-gray-800/50 rounded-lg p-3 text-center border border-gray-700">
            <div className="flex items-center justify-center gap-2 mb-1">
              <div className="w-3 h-3 rounded-full bg-blue-500" />
              <span className="text-gray-400 text-xs uppercase tracking-wide">Other Owned</span>
            </div>
            <div className="text-xl font-bold text-blue-400 font-mono">{formatNumber(Math.max(0, otherOwnedShares))}</div>
            <div className="text-xs text-gray-500">{formatPercentage(Math.max(0, otherOwnedPercent))}</div>
          </div>
          
          <div className="bg-gray-800/50 rounded-lg p-3 text-center border border-gray-700">
            <div className="flex items-center justify-center gap-2 mb-1">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.available }} />
              <span className="text-gray-400 text-xs uppercase tracking-wide">Available</span>
            </div>
            <div className="text-xl font-bold text-gray-300 font-mono">{formatNumber(availableShares)}</div>
            <div className="text-xs text-gray-500">{formatPercentage(availablePercent)}</div>
          </div>
        </div>

        {/* Visual Progress Bar */}
        <div className="h-8 bg-gray-700 rounded-lg overflow-hidden flex border border-gray-600">
          {yourPercent > 0 && (
            <div 
              className="h-full flex items-center justify-center transition-all duration-500"
              style={{ width: `${yourPercent}%`, backgroundColor: COLORS.owned }}
            >
              {yourPercent > 10 && <span className="text-white text-xs font-bold">{formatPercentage(yourPercent)}</span>}
            </div>
          )}
          {onSalePercent > 0 && (
            <div 
              className="h-full flex items-center justify-center transition-all duration-500"
              style={{ width: `${onSalePercent}%`, backgroundColor: COLORS.onSale }}
            >
              {onSalePercent > 10 && <span className="text-white text-xs font-bold">{formatPercentage(onSalePercent)}</span>}
            </div>
          )}
          {otherOwnedPercent > 0 && (
            <div 
              className="h-full flex items-center justify-center transition-all duration-500 bg-blue-500"
              style={{ width: `${Math.max(0, otherOwnedPercent)}%` }}
            >
              {otherOwnedPercent > 10 && <span className="text-white text-xs font-bold">{formatPercentage(otherOwnedPercent)}</span>}
            </div>
          )}
          {availablePercent > 0 && (
            <div 
              className="h-full flex items-center justify-center transition-all duration-500"
              style={{ width: `${availablePercent}%`, backgroundColor: COLORS.available }}
            >
              {availablePercent > 10 && <span className="text-white text-xs font-bold">{formatPercentage(availablePercent)}</span>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const VisualTrailerOwnership = ({ 
  totalShares = 100,
  yourShares = 0,
  onSaleShares = 0,
  soldShares = 0,
  className = '' 
}) => {
  const availableShares = totalShares - soldShares;
  const otherOwnedShares = soldShares - yourShares - onSaleShares;
  
  // Calculate percentages
  const yourPercent = (yourShares / totalShares) * 100;
  const onSalePercent = (onSaleShares / totalShares) * 100;
  const availablePercent = (availableShares / totalShares) * 100;
  const otherOwnedPercent = (otherOwnedShares / totalShares) * 100;

  return (
    <div className={`relative ${className}`} data-testid="visual-trailer-ownership">
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl p-6 md:p-8">
        {/* Trailer SVG - Clean line art style matching reference image */}
        <svg viewBox="0 0 800 320" className="w-full h-auto mb-4">
          <defs>
            <linearGradient id="trailerOwnershipGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor={COLORS.owned} />
              <stop offset={`${yourPercent}%`} stopColor={COLORS.owned} />
              <stop offset={`${yourPercent}%`} stopColor={COLORS.onSale} />
              <stop offset={`${yourPercent + onSalePercent}%`} stopColor={COLORS.onSale} />
              <stop offset={`${yourPercent + onSalePercent}%`} stopColor="#3B82F6" />
              <stop offset={`${yourPercent + onSalePercent + otherOwnedPercent}%`} stopColor="#3B82F6" />
              <stop offset={`${yourPercent + onSalePercent + otherOwnedPercent}%`} stopColor={COLORS.available} />
              <stop offset="100%" stopColor={COLORS.available} />
            </linearGradient>
          </defs>

          {/* TRAILER BODY - Clean box style like reference */}
          <g stroke={COLORS.line} strokeWidth="2.5" fill="none">
            {/* Main trailer body - rectangular box */}
            <rect
              x="100"
              y="100"
              width="600"
              height="120"
              fill="url(#trailerOwnershipGradient)"
              rx="3"
            />
            
            {/* Top highlight */}
            <line x1="105" y1="105" x2="695" y2="105" stroke="#fff" strokeWidth="1" opacity="0.2" />
            
            {/* Panel/Section lines */}
            <line x1="250" y1="102" x2="250" y2="218" strokeWidth="1.5" opacity="0.3" />
            <line x1="400" y1="102" x2="400" y2="218" strokeWidth="1.5" opacity="0.3" />
            <line x1="550" y1="102" x2="550" y2="218" strokeWidth="1.5" opacity="0.3" />
            
            {/* Front kingpin connection */}
            <rect x="75" y="150" width="30" height="30" fill="#4B5563" rx="2" />
            <circle cx="90" cy="165" r="6" fill="#374151" />
            
            {/* Rear door */}
            <rect x="685" y="110" width="10" height="100" fill="#374151" rx="1" />
            <rect x="688" y="145" width="4" height="25" fill="#6B7280" rx="1" />
            
            {/* Undercarriage */}
            <line x1="105" y1="222" x2="695" y2="222" strokeWidth="1.5" opacity="0.4" />
            
            {/* FRONT LANDING GEAR */}
            {/* Left leg */}
            <rect x="150" y="220" width="8" height="55" fill="#4B5563" strokeWidth="2" />
            <rect x="145" y="272" width="18" height="6" fill="#6B7280" strokeWidth="1.5" rx="1" />
            
            {/* Right leg */}
            <rect x="195" y="220" width="8" height="55" fill="#4B5563" strokeWidth="2" />
            <rect x="190" y="272" width="18" height="6" fill="#6B7280" strokeWidth="1.5" rx="1" />
            
            {/* Crank mechanism */}
            <circle cx="177" cy="235" r="5" fill="#9CA3AF" />
            <line x1="177" y1="235" x2="165" y2="250" strokeWidth="2" />
            
            {/* REAR WHEELS - 2 tandem axle wheels side by side */}
            {/* Left wheel */}
            <circle cx="580" cy="235" r="24" fill="#1F2937" strokeWidth="3" />
            <circle cx="580" cy="235" r="15" fill="#4B5563" />
            <circle cx="580" cy="235" r="6" fill="#9CA3AF" />
            {/* Wheel spokes/details */}
            <line x1="580" y1="215" x2="580" y2="255" stroke="#374151" strokeWidth="1" />
            <line x1="560" y1="235" x2="600" y2="235" stroke="#374151" strokeWidth="1" />
            
            {/* Right wheel (next to left) */}
            <circle cx="640" cy="235" r="24" fill="#1F2937" strokeWidth="3" />
            <circle cx="640" cy="235" r="15" fill="#4B5563" />
            <circle cx="640" cy="235" r="6" fill="#9CA3AF" />
            {/* Wheel spokes/details */}
            <line x1="640" y1="215" x2="640" y2="255" stroke="#374151" strokeWidth="1" />
            <line x1="620" y1="235" x2="660" y2="235" stroke="#374151" strokeWidth="1" />
            
            {/* Axle connecting wheels */}
            <line x1="580" y1="235" x2="640" y2="235" stroke="#374151" strokeWidth="3" />
          </g>
          
          {/* Ground line */}
          <line x1="50" y1="278" x2="750" y2="278" stroke="#374151" strokeWidth="1.5" opacity="0.3" />
        </svg>

        {/* Ownership Breakdown Legend */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="bg-gray-800/50 rounded-lg p-3 text-center border border-gray-700">
            <div className="flex items-center justify-center gap-2 mb-1">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.owned }} />
              <span className="text-gray-400 text-xs uppercase tracking-wide">You Own</span>
            </div>
            <div className="text-xl font-bold text-green-400 font-mono">{formatNumber(yourShares)}</div>
            <div className="text-xs text-gray-500">{formatPercentage(yourPercent)}</div>
          </div>
          
          <div className="bg-gray-800/50 rounded-lg p-3 text-center border border-gray-700">
            <div className="flex items-center justify-center gap-2 mb-1">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.onSale }} />
              <span className="text-gray-400 text-xs uppercase tracking-wide">On Sale</span>
            </div>
            <div className="text-xl font-bold text-amber-400 font-mono">{formatNumber(onSaleShares)}</div>
            <div className="text-xs text-gray-500">{formatPercentage(onSalePercent)}</div>
          </div>
          
          <div className="bg-gray-800/50 rounded-lg p-3 text-center border border-gray-700">
            <div className="flex items-center justify-center gap-2 mb-1">
              <div className="w-3 h-3 rounded-full bg-blue-500" />
              <span className="text-gray-400 text-xs uppercase tracking-wide">Other Owned</span>
            </div>
            <div className="text-xl font-bold text-blue-400 font-mono">{formatNumber(Math.max(0, otherOwnedShares))}</div>
            <div className="text-xs text-gray-500">{formatPercentage(Math.max(0, otherOwnedPercent))}</div>
          </div>
          
          <div className="bg-gray-800/50 rounded-lg p-3 text-center border border-gray-700">
            <div className="flex items-center justify-center gap-2 mb-1">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.available }} />
              <span className="text-gray-400 text-xs uppercase tracking-wide">Available</span>
            </div>
            <div className="text-xl font-bold text-gray-300 font-mono">{formatNumber(availableShares)}</div>
            <div className="text-xs text-gray-500">{formatPercentage(availablePercent)}</div>
          </div>
        </div>

        {/* Visual Progress Bar */}
        <div className="h-8 bg-gray-700 rounded-lg overflow-hidden flex border border-gray-600">
          {yourPercent > 0 && (
            <div 
              className="h-full flex items-center justify-center transition-all duration-500"
              style={{ width: `${yourPercent}%`, backgroundColor: COLORS.owned }}
            >
              {yourPercent > 10 && <span className="text-white text-xs font-bold">{formatPercentage(yourPercent)}</span>}
            </div>
          )}
          {onSalePercent > 0 && (
            <div 
              className="h-full flex items-center justify-center transition-all duration-500"
              style={{ width: `${onSalePercent}%`, backgroundColor: COLORS.onSale }}
            >
              {onSalePercent > 10 && <span className="text-white text-xs font-bold">{formatPercentage(onSalePercent)}</span>}
            </div>
          )}
          {otherOwnedPercent > 0 && (
            <div 
              className="h-full flex items-center justify-center transition-all duration-500 bg-blue-500"
              style={{ width: `${Math.max(0, otherOwnedPercent)}%` }}
            >
              {otherOwnedPercent > 10 && <span className="text-white text-xs font-bold">{formatPercentage(otherOwnedPercent)}</span>}
            </div>
          )}
          {availablePercent > 0 && (
            <div 
              className="h-full flex items-center justify-center transition-all duration-500"
              style={{ width: `${availablePercent}%`, backgroundColor: COLORS.available }}
            >
              {availablePercent > 10 && <span className="text-white text-xs font-bold">{formatPercentage(availablePercent)}</span>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
